import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { formatAlertTitle } from '../utilities/siteAlerts'
import './serviceStatus.scss'

const SUPPORT_LINK_CLASS = 'text-red underline break-words'

/**
 * How to reach Red Funnel while the booking system is unavailable. Hardcoded
 * rather than taken from the alert feed: this is the one thing on the page that
 * must still be right when the feed only says the site is down, and a maintenance
 * page with no way to contact anyone is worse than no page at all.
 */
const UrgentSupport = () => (
  <div className="bg-white border border-solid border-lighter-grey px-6 py-8 md:px-12 md:py-10 text-left">
    <h2 className="text-3xl md:text-5xl mb-6">Need Urgent Support?</h2>

    <h3 className="text-red text-xl md:text-2xl mb-4">Call Us:</h3>
    <p className="text-text leading-8 mb-4">
      Contact Centre:{' '}
      <a href="tel:+442380019192" className={SUPPORT_LINK_CLASS}>
        023 8001 9192
      </a>
    </p>
    <p className="text-text leading-8 mb-4">
      Opening Hours: Mon-Fri 08:00-19:00
    </p>
    <p className="text-text leading-8 mb-8">
      Weekends and Bank Holidays 08:00-18:00
    </p>

    <h3 className="text-red text-xl md:text-2xl mb-4">Email Us:</h3>
    <p className="text-text leading-8 mb-4">
      Bookings &amp; General Enquiries:{' '}
      <a href="mailto:post@redfunnel.co.uk" className={SUPPORT_LINK_CLASS}>
        post@redfunnel.co.uk
      </a>
    </p>
    <p className="text-text leading-8 mb-4">
      Customer Service Enquiries:{' '}
      <a
        href="mailto:customerservices@redfunnel.co.uk"
        className={SUPPORT_LINK_CLASS}
      >
        customerservices@redfunnel.co.uk
      </a>
    </p>
    <p className="text-text leading-8 mb-4">
      Red Funnel Holidays:{' '}
      <a href="mailto:holidays@redfunnel.co.uk" className={SUPPORT_LINK_CLASS}>
        holidays@redfunnel.co.uk
      </a>
    </p>
    <p className="text-text leading-8">
      When contacting us via email, please include any relevant information,
      such as your booking number (if you have one) and main contact details.
    </p>
  </div>
)

/**
 * maintenance.serviceStatusContent from the feed - Drupal's own service status
 * table, injected as authored.
 *
 * It ships as a tab widget: a nav of buttons carrying data-tab-control, and two
 * .tab panels whose visibility is set by inline styles (tab-1 block, tab-2 none).
 * Drupal's JavaScript is what swaps them, and none of it is loaded here - so
 * without this the "From Isle of Wight" table can never be reached. The listeners
 * below reproduce just that behaviour; serviceStatus.scss covers the styling the
 * markup likewise expects to find.
 */
const ServiceStatus = ({ content }) => {
  const containerRef = useRef(null)

  useEffect(() => {
    const root = containerRef.current
    if (!root) return undefined

    const buttons = Array.from(root.querySelectorAll('[data-tab-control]'))
    const showTab = key => {
      buttons.forEach(button =>
        button.classList.toggle(
          'inactive',
          button.getAttribute('data-tab-control') !== key,
        ),
      )
      root.querySelectorAll('.tab').forEach(tab => {
        // set as an attribute rather than via tab.style, both to satisfy
        // no-param-reassign and because it replaces the inline style Drupal
        // shipped, which a stylesheet rule could not override
        tab.setAttribute(
          'style',
          tab.classList.contains(`tab-${key}`)
            ? 'display: block'
            : 'display: none',
        )
      })
    }

    const listeners = buttons.map(button => {
      const handleClick = event => {
        // the buttons are hrefs of "#", which would otherwise jump the page
        event.preventDefault()
        showTab(button.getAttribute('data-tab-control'))
      }
      button.addEventListener('click', handleClick)
      return { button, handleClick }
    })

    return () =>
      listeners.forEach(({ button, handleClick }) =>
        button.removeEventListener('click', handleClick),
      )
  }, [content])

  return (
    <div
      ref={containerRef}
      className="service-status-content mt-12 bg-white border border-solid border-lighter-grey px-6 py-8 md:px-12 md:py-10 text-left text-text leading-8 overflow-x-auto [&_p]:mb-4 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_a]:text-red"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

ServiceStatus.propTypes = { content: PropTypes.string.isRequired }

/**
 * Replaces the booking app while a maintenance alert is live. There is
 * deliberately nothing to interact with - no retry, no dismiss - because the
 * point is that bookings cannot be taken until the window has passed.
 *
 * The message is authored HTML from the same Drupal feeds the rest of the app
 * renders this way (see MarketingPage, HtmlContentToggle). The support details
 * below it are not - see UrgentSupport.
 */
const MaintenanceMode = ({ alert, serviceStatusContent }) => (
  <div className="flex-1 bg-slightly-off-white px-4 py-16 md:py-24">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-4xl md:text-6xl mb-8">
        {formatAlertTitle(alert?.title) || 'Site Maintenance'}
      </h1>
      {alert?.message ? (
        <div
          className="maintenance-message text-text leading-8 [&_p]:mb-6 [&_a]:text-blue [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: alert.message }}
        />
      ) : null}
      <div className="mt-20">
        <UrgentSupport />
      </div>
      {serviceStatusContent ? (
        <>
          <h2 className="text-3xl md:text-5xl my-16 uppercase">
            live service status
          </h2>
          <ServiceStatus content={serviceStatusContent} />
        </>
      ) : null}
    </div>
  </div>
)

MaintenanceMode.defaultProps = { alert: null, serviceStatusContent: '' }

MaintenanceMode.propTypes = {
  alert: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
  }),
  serviceStatusContent: PropTypes.string,
}

export default MaintenanceMode
